const DRAFT_KEY = 'recruiter:program-create-draft';

export interface ProgramCreateDraft {
  companyId?: string;
  /** Cached display label for the picked company, so the Combobox trigger can re-render after a reload without re-fetching. */
  companyName?: string;
  name?: string;
  description?: string;
  contractType?: string;
  startDate?: string;
  endDate?: string;
  targetRegistrations?: string;
  selectedBenefits?: string[];
  selectedAdditionalPayments?: string[];
}

export function loadProgramDraft(): ProgramCreateDraft {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as ProgramCreateDraft) : {};
  } catch {
    return {};
  }
}

export function saveProgramDraft(patch: Partial<ProgramCreateDraft>): void {
  const current = loadProgramDraft();
  sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...current, ...patch }));
}

export function clearProgramDraft(): void {
  sessionStorage.removeItem(DRAFT_KEY);
  pendingCoverFile = null;
  pendingNameError = null;
  pendingHiddenDefaultFaqIds = [];
}

// ─── FAQs padrão ocultadas (EU-641, criação — FQC-014/015) ───────────────────
//
// Na CRIAÇÃO o programa ainda não existe: as escolhas do painel ficam AQUI em
// memória e são persistidas junto com a criação (PUT hidden-default-faqs após
// o POST). Abandonar a criação (clearProgramDraft) descarta tudo — mesma
// semântica do pendingCoverFile.

let pendingHiddenDefaultFaqIds: number[] = [];

export function setPendingHiddenDefaultFaqIds(ids: number[]): void {
  pendingHiddenDefaultFaqIds = [...ids];
}

export function getPendingHiddenDefaultFaqIds(): number[] {
  return [...pendingHiddenDefaultFaqIds];
}

// ─── Cover file (Step 1 → Step 3 hand-off) ────────────────────────────────────
//
// `File` isn't JSON-serializable, so it can't live in sessionStorage with the
// rest of the draft. We stash it in a module-level variable that survives the
// route changes between Step 1, 2 and 3 (same tab, same SPA bundle). A full
// page reload between steps drops it — same trade-off the rest of the draft
// would have had if sessionStorage could hold a File.

let pendingCoverFile: File | null = null;

export function setPendingCoverFile(file: File | null): void {
  pendingCoverFile = file;
}

export function getPendingCoverFile(): File | null {
  return pendingCoverFile;
}

// ─── Server-side dup name error hand-off (Step 3 → Step 1) ────────────────────
//
// When the BFF returns 409 `duplicate_program_name` at the final POST (Step 3),
// the consultor needs to see the same inline error they'd have gotten on the
// Step 1 pre-flight check. Stash the message here, navigate back to Step 1,
// and let Step 1's mount-effect pick it up + render it next to the field.
// One-shot: read clears the value so a subsequent route mount doesn't repeat.

let pendingNameError: string | null = null;

export function setPendingNameError(message: string | null): void {
  pendingNameError = message;
}

export function consumePendingNameError(): string | null {
  const value = pendingNameError;
  pendingNameError = null;
  return value;
}
